import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ReCAPTCHA from "react-google-recaptcha";

const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_KEY;

let apiUrl = "https://api.gimmefy.ai/v2";

console.log("import.meta.env.VITE_API_URL", import.meta.env.VITE_API_URL)
const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

const TellAFriendForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [formData, setFormData] = useState({
    invitedByName: "",
    invitedToName: "",
    invitedToEmail: ""
  });

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.invitedByName || !formData.invitedToName || !formData.invitedToEmail) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.invitedToEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      toast({
        title: "reCAPTCHA Error",
        description: "Please complete the reCAPTCHA verification",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        event_name: "TELL_A_FRIEND_INVITATION",
        payload: {
          invited_by_name: formData.invitedByName,
          invited_to_name: formData.invitedToName,
          invited_to_email: formData.invitedToEmail
        },
        captcha_code: captchaValue
      };

      // Direct API call using fetch (same as enterprise form)
      const response = await fetch(`${apiUrl}/triggerNotificationPublic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send invitation');
      }

      toast({
        title: "Invitation Sent!",
        description: "Your friend will receive a special signup link with bonus credits.",
        action: <div style={{ display: 'none' }} />,
      });

      // Reset form and reCAPTCHA
      setFormData({
        invitedByName: "",
        invitedToName: "",
        invitedToEmail: ""
      });
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (err) {
      console.error("Error sending tell a friend invitation:", err);
      toast({
        title: "Error",
        description: "Failed to send invitation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card className="p-8 bg-gradient-card border-0 shadow-elegant">
      <form id="tell-a-friend-form" onSubmit={handleSubmit} className="space-y-6 text-left">
        <div className="space-y-2">
          <Label htmlFor="invited-by-name" className="text-sm font-medium text-foreground text-left">
            Your name *
          </Label>
          <Input
            id="invited-by-name"
            type="text"
            value={formData.invitedByName}
            onChange={(e) => handleInputChange("invitedByName", e.target.value)}
            placeholder="Enter your name"
            className="w-full"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="invited-to-name" className="text-sm font-medium text-foreground text-left">
            Your friend's name *
          </Label>
          <Input
            id="invited-to-name"
            type="text"
            value={formData.invitedToName}
            onChange={(e) => handleInputChange("invitedToName", e.target.value)}
            placeholder="Enter your friend's name"
            className="w-full"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="invited-to-email" className="text-sm font-medium text-foreground text-left">
            Your friend's email address *
          </Label>
          <Input
            id="invited-to-email"
            type="email"
            value={formData.invitedToEmail}
            onChange={(e) => handleInputChange("invitedToEmail", e.target.value)}
            placeholder="Enter your friend's email address"
            className="w-full"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* reCAPTCHA */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Security Verification</span>
          </div>
          <ReCAPTCHA
            id="tell-a-friend-recaptcha"
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            onChange={handleCaptchaChange}
            theme="light"
          />
          {!captchaValue && (
            <p className="text-sm text-muted-foreground mt-2">
              Please complete the reCAPTCHA verification above
            </p>
          )}
        </div>

        <Button
          id="tell-a-friend-submit-button"
          type="submit"
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground group"
          size="lg"
          disabled={isSubmitting || !captchaValue || !formData.invitedByName || !formData.invitedToName || !formData.invitedToEmail}
        >
          {isSubmitting ? "Sending..." : "Send Message"}
          {!isSubmitting && <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />}
        </Button>
      </form>
    </Card>
  );
};

export default TellAFriendForm;
